import { Link } from "@tanstack/react-router";
import { useState, useRef, useEffect } from "react";
import { Menu, X, ChevronDown } from "lucide-react";
import { pathFor, type PageId } from "@/i18n/slugMap";
import { shared, type SharedStrings } from "@/i18n/shared";
import { useLocale } from "@/i18n/LocaleProvider";
import { LanguageSwitcher } from "./LanguageSwitcher";

type NavKey = keyof SharedStrings["nav"];
type NavChild =
  | { id: PageId; labelKey: NavKey }
  | { external: string; labelKey: NavKey };
type NavItem =
  | { id: PageId; labelKey: NavKey; children?: NavChild[] }
  | { external: string; labelKey: NavKey; children?: NavChild[] };

const EMPLY_URL =
  "https://misupport.career.emply.com/da/?utm_source=misupport_web&utm_medium=web";

const navItems: NavItem[] = [
  { id: "home", labelKey: "home" },
  {
    id: "ydelser",
    labelKey: "services",
    children: [
      { id: "ydelser", labelKey: "allServices" },
      { id: "projektledelse", labelKey: "projektledelse" },
      { id: "byggeledelse", labelKey: "byggeledelse" },
      { id: "bygherreraadgivning", labelKey: "bygherreraadgivning" },
      { id: "tidsplanlaegning", labelKey: "tidsplanlaegning" },
      { id: "projektoekonomi", labelKey: "projektoekonomi" },
      { id: "projektassistance", labelKey: "projektassistance" },
      { id: "shutdown", labelKey: "shutdown" },
      { id: "hse", labelKey: "hse" },
      { id: "sitecompliance", labelKey: "siteCompliance" },
    ],
  },
  {
    id: "brancher",
    labelKey: "industries",
    children: [
      { id: "brancher", labelKey: "allIndustries" },
      { id: "pharma", labelKey: "pharma" },
      { id: "industri", labelKey: "industri" },
      { id: "datacentre", labelKey: "datacentre" },
      { id: "energi", labelKey: "energi" },
      { id: "hospitaler", labelKey: "hospitaler" },
      { id: "kontor", labelKey: "kontor" },
      { id: "transport", labelKey: "transport" },
    ],
  },
  {
    external: EMPLY_URL,
    labelKey: "career",
    children: [
      { external: EMPLY_URL, labelKey: "openPositions" },
      { id: "arbejdsplads", labelKey: "workplace" },
      { id: "modenmisupporter", labelKey: "meetSupporter" },
    ],
  },
  { id: "samarbejdspartnere", labelKey: "partners" },
  {
    id: "omos",
    labelKey: "about",
    children: [
      { id: "omos", labelKey: "aboutMi" },
      { id: "esg", labelKey: "esg" },
      { id: "viden", labelKey: "viden" },
      { id: "samarbejdspartnere", labelKey: "partners" },
      { external: "https://www.misupportit.dk/", labelKey: "misupportIt" },
    ],
  },
];

export function SiteHeader() {
  const locale = useLocale();
  const t = shared[locale];
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [openMobileSub, setOpenMobileSub] = useState<string | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  const handleEnter = (key: string) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenMenu(key);
  };
  const handleLeave = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpenMenu(null), 120);
  };

  return (
    <header className="sticky top-0 z-40 w-full bg-background/95 backdrop-blur border-b border-white/10">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 md:px-8">
        <Link to={pathFor("home", locale)} className="flex items-center gap-2" aria-label="MI Support">
          <img
            src="/wp-content/uploads/MI-Support-Logo-White.svg"
            alt="MI Support"
            width={498}
            height={104}
            className="h-9 md:h-10 w-auto"
            loading="eager"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) =>
            item.children ? (
              <div
                key={item.labelKey}
                className="relative"
                onMouseEnter={() => handleEnter(item.labelKey)}
                onMouseLeave={handleLeave}
              >
                {"external" in item ? (
                  <a
                    href={item.external}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    {t.nav[item.labelKey]}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </a>
                ) : (
                  <Link
                    to={pathFor(item.id, locale)}
                    className="inline-flex items-center gap-1 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                    activeProps={{ className: "text-foreground font-semibold" }}
                  >
                    {t.nav[item.labelKey]}
                    <ChevronDown className="h-3.5 w-3.5" />
                  </Link>
                )}
                {openMenu === item.labelKey && (
                  <div className="absolute left-0 top-full pt-3 min-w-[220px]">
                    <div className="rounded-xl bg-card border border-white/10 shadow-xl py-2">
                      {item.children.map((c) =>
                        "external" in c ? (
                          <a
                            key={c.labelKey}
                            href={c.external}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-4 py-2.5 text-sm text-foreground/85 hover:text-foreground hover:bg-white/5"
                            onClick={() => setOpenMenu(null)}
                          >
                            {t.nav[c.labelKey]}
                          </a>
                        ) : (
                          <Link
                            key={c.labelKey}
                            to={pathFor(c.id, locale)}
                            className="block px-4 py-2.5 text-sm text-foreground/85 hover:text-foreground hover:bg-white/5"
                            onClick={() => setOpenMenu(null)}
                          >
                            {t.nav[c.labelKey]}
                          </Link>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            ) : "external" in item ? (
              <a
                key={item.labelKey}
                href={item.external}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
              >
                {t.nav[item.labelKey]}
              </a>
            ) : (
              <Link
                key={item.labelKey}
                to={pathFor(item.id, locale)}
                className="text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                activeProps={{ className: "text-foreground font-semibold" }}
                activeOptions={{ exact: item.id === "home" }}
              >
                {t.nav[item.labelKey]}
              </Link>
            ),
          )}
          <Link
            to={pathFor("kontakt", locale)}
            className="rounded-2xl bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
          >
            {t.cta.contact}
          </Link>
          <LanguageSwitcher className="ml-2" />
        </nav>

        {/* Mobile toggle */}
        <button
          className="lg:hidden text-foreground p-2"
          onClick={() => setOpen(!open)}
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {/* Mobile nav */}
      {open && (
        <nav className="lg:hidden border-t border-white/10 px-4 py-4 flex flex-col gap-1">
          {navItems.map((item) =>
            item.children ? (
              <div key={item.labelKey} className="border-b border-white/5 last:border-0">
                <div className="flex items-center justify-between">
                  {"external" in item ? (
                    <a
                      href={item.external}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setOpen(false)}
                      className="text-foreground/90 py-3 flex-1"
                    >
                      {t.nav[item.labelKey]}
                    </a>
                  ) : (
                    <Link
                      to={pathFor(item.id, locale)}
                      onClick={() => setOpen(false)}
                      className="text-foreground/90 py-3 flex-1"
                      activeProps={{ className: "text-foreground font-semibold" }}
                    >
                      {t.nav[item.labelKey]}
                    </Link>
                  )}
                  <button
                    aria-label={t.nav[item.labelKey]}
                    onClick={() =>
                      setOpenMobileSub(openMobileSub === item.labelKey ? null : item.labelKey)
                    }
                    className="p-2"
                  >
                    <ChevronDown
                      className={`h-4 w-4 transition-transform ${
                        openMobileSub === item.labelKey ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                </div>
                {openMobileSub === item.labelKey && (
                  <div className="pl-4 pb-3 flex flex-col gap-1">
                    {item.children.map((c) =>
                      "external" in c ? (
                        <a
                          key={c.labelKey}
                          href={c.external}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="py-2 text-sm text-foreground/75"
                          onClick={() => setOpen(false)}
                        >
                          {t.nav[c.labelKey]}
                        </a>
                      ) : (
                        <Link
                          key={c.labelKey}
                          to={pathFor(c.id, locale)}
                          className="py-2 text-sm text-foreground/75"
                          onClick={() => setOpen(false)}
                        >
                          {t.nav[c.labelKey]}
                        </Link>
                      ),
                    )}
                  </div>
                )}
              </div>
            ) : "external" in item ? (
              <a
                key={item.labelKey}
                href={item.external}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="text-foreground/90 py-3 border-b border-white/5 last:border-0"
              >
                {t.nav[item.labelKey]}
              </a>
            ) : (
              <Link
                key={item.labelKey}
                to={pathFor(item.id, locale)}
                onClick={() => setOpen(false)}
                className="text-foreground/90 py-3 border-b border-white/5 last:border-0"
                activeProps={{ className: "text-foreground font-semibold" }}
                activeOptions={{ exact: item.id === "home" }}
              >
                {t.nav[item.labelKey]}
              </Link>
            ),
          )}
          <Link
            to={pathFor("kontakt", locale)}
            onClick={() => setOpen(false)}
            className="mt-4 rounded-2xl bg-primary px-6 py-2.5 text-center text-sm font-semibold text-primary-foreground"
          >
            {t.cta.contact}
          </Link>
          <LanguageSwitcher className="mt-4 justify-center" />
        </nav>
      )}
    </header>
  );
}
